import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Zap, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      toast.success("Bem-vindo!");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no login");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background text-foreground grid lg:grid-cols-2"
      data-testid="login-page"
    >
      {/* Visual left side */}
      <div
        className="hidden lg:block relative overflow-hidden"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1633602114554-be8c2c5d6828?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYXJjaGl0ZWN0dXJlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc2NjQwNDMwfDA&ixlib=rb-4.1.0&q=85")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-background/85 via-background/50 to-background/95" />
        <div className="absolute inset-0 grain-bg" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary grid place-items-center pix-glow">
              <Zap className="w-6 h-6 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Painel de Vendas PIX
              </p>
              <p className="font-display font-bold text-lg">MACHADO LIMA</p>
            </div>
          </div>

          <div className="max-w-md">
            <h1 className="font-display text-5xl font-bold leading-[1.05] mb-4">
              Controle total das suas vendas
              <span className="text-primary"> PIX</span>.
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed">
              Gere cobranças em segundos, receba via Woovi, emita recibos
              automáticos e acompanhe suas metas em tempo real.
            </p>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-primary" />
            Proteção contra contestações PIX MED · Recibos em PDF
          </div>
        </div>
      </div>

      {/* Form right side */}
      <div className="flex items-center justify-center p-6 lg:p-12 relative">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-xl bg-primary grid place-items-center">
              <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                ML PIX
              </p>
              <p className="font-display font-bold">Painel</p>
            </div>
          </div>

          <h2 className="font-display text-3xl font-bold mb-2">Entrar</h2>
          <p className="text-sm text-muted-foreground mb-10">
            Acesse o painel com seu usuário e senha.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs uppercase tracking-wider text-muted-foreground">
                Usuário
              </Label>
              <Input
                id="username"
                data-testid="login-username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="h-12 bg-secondary/40 border-border focus-visible:ring-primary"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                Senha
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-secondary/40 border-border focus-visible:ring-primary"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Entrando...
                </>
              ) : (
                "Entrar no painel"
              )}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-10 text-center">
            Credenciais padrão:{" "}
            <span className="text-foreground font-medium">admin / 20404242</span>
            <br />
            Você pode alterá-las em Configurações &gt; Segurança.
          </p>
        </div>
      </div>
    </div>
  );
}
